/**
 * 未知バーコードの紐付けシート（SPEC §11.2）。
 *
 * 自己登録方式（SPEC §11.1）なので商品名は外部 DB から取れない。
 * 「既存品目から選ぶ」か「その場で新しい品目を作る」かの 2 択だけを出す。
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { ItemRow } from '../../shared/types';
import { formatBarcode } from '../lib/barcode';
import { Sheet } from './Sheet';

export interface LinkBarcodeSheetProps {
  barcode: string;
  /** 紐付け先の候補（バーコード未設定の品目が上に来る） */
  items: ItemRow[];
  onClose: () => void;
  onPick: (item: ItemRow) => void;
  onCreate: (name: string) => void;
}

export function LinkBarcodeSheet({
  barcode,
  items,
  onClose,
  onPick,
  onCreate,
}: LinkBarcodeSheetProps): ReactNode {
  const [query, setQuery] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim();
    const list = q === '' ? items : items.filter((i) => i.name.includes(q));
    // 既に別コードが付いている品目は付け替え（解除→再紐付け）が要るので後ろへ回す
    return list.slice().sort((a, b) => Number(a.barcode !== null) - Number(b.barcode !== null));
  }, [items, query]);

  const trimmedNew = newName.trim();
  const canCreate = trimmedNew !== '' && trimmedNew.length <= 200;

  return (
    <Sheet
      title="バーコードを紐付け"
      onClose={onClose}
      footer={
        creating ? (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn-ghost" onClick={() => setCreating(false)}>
              戻る
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!canCreate}
              onClick={() => onCreate(trimmedNew)}
            >
              作成して紐付け
            </button>
          </div>
        ) : (
          <button type="button" className="btn-ghost w-full" onClick={() => setCreating(true)}>
            ＋ 新しい品目として登録
          </button>
        )
      }
    >
      <div className="space-y-4">
        <div className="rounded-2xl border border-ink-700 bg-ink-900 px-4 py-3 text-center">
          <p className="text-[11px] text-zinc-500">読み取ったバーコード</p>
          <p className="mt-0.5 text-lg font-bold tabular-nums tracking-wide">
            {formatBarcode(barcode)}
          </p>
          <p className="mt-1 text-[11px] text-zinc-500">
            この番号はまだどの品目にも紐付いていません。
          </p>
        </div>

        {creating ? (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-400" htmlFor="link-new-name">
              品目名<span className="ml-1 text-orange-400">必須</span>
            </label>
            <input
              id="link-new-name"
              className="field text-lg"
              autoFocus
              placeholder="シャンプー"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-zinc-500">
              カテゴリや想定使用日数は後から品目詳細で変えられます。
            </p>
          </div>
        ) : (
          <>
            {items.length > 3 && (
              <input
                className="field"
                aria-label="品目を検索"
                placeholder="品目を検索"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            )}

            {filtered.length === 0 ? (
              <p className="py-6 text-center text-xs text-zinc-500">
                {items.length === 0
                  ? 'まだ品目がありません。下のボタンから登録してください。'
                  : '一致する品目がありません。'}
              </p>
            ) : (
              <ul className="divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700">
                {filtered.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onPick(item)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{item.name}</span>
                        <span className="block text-[11px] text-zinc-500">
                          {item.category ?? '未分類'}
                          {item.barcode !== null && ` ・別のコードを紐付け済み（置き換えます）`}
                        </span>
                      </span>
                      <svg
                        viewBox="0 0 20 20"
                        className="size-4 shrink-0 text-zinc-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}
